import { CommonModule } from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	Output,
	QueryList,
	ViewChildren,
	OnInit,
	OnDestroy,
	ViewChild,
	AfterViewInit
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { PrismAutoTooltipComponent } from '@shure/prism-angular-components';
import { getDeviceNameValidators } from '@shure/shared/angular/ui/validators/common';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

@Component({
	selector: 'sh-inline-device-name-input',
	templateUrl: './inline-device-name-input.component.html',
	styleUrls: ['./inline-device-name-input.component.scss'],
	imports: [CommonModule, TranslocoModule, MatInputModule, ReactiveFormsModule, PrismAutoTooltipComponent],
	providers: [
		provideInlineScope('scInlineDeviceNameInput', /* istanbul ignore next */ (lang) => import(`../i18n/${lang}`))
	]
})
export class InlineDeviceNameInputComponent implements OnInit, OnDestroy, AfterViewInit {
	/**
	 * Id incrementer allowing for multiple <sh-inline-device-name-inputt> in the same view.
	 * @ignore internal
	 */
	private static nextId = 0;

	private readonly destroy$ = new Subject<void>();

	private _disabled = false;

	private _maxLength!: number;
	private _nameConstraint: string | undefined;

	/**
	 * @ignore internal
	 */
	@HostBinding()
	public readonly id = `sh-online-devices-inline-device-name-input-${InlineDeviceNameInputComponent.nextId++}`;

	@ViewChild('input') public inputElement!: ElementRef<HTMLInputElement>;
	@ViewChildren('error') public errors!: QueryList<ElementRef>;

	@Input()
	public set value(name: string) {
		this.nameControl.setValue(name, { emitEvent: false });
	}

	@Input()
	public set nameConstraint(nameConstraint: string | undefined) {
		this._nameConstraint = nameConstraint;
		this.nameControl.markAsPristine();

		this.nameControl.setValidators(getDeviceNameValidators(this.maxLength, nameConstraint));
		this.nameControl.updateValueAndValidity();
	}

	public get nameConstraint(): string | undefined {
		return this._nameConstraint;
	}

	@Input()
	public set maxLength(maxLength: number) {
		this._maxLength = maxLength;

		this.nameControl.markAsPristine();
		this.nameControl.setValidators(getDeviceNameValidators(maxLength, this.nameConstraint));
		this.nameControl.updateValueAndValidity();
	}

	public get maxLength(): number {
		return this._maxLength;
	}

	@Input()
	public set disabled(isDisabled: boolean) {
		this._disabled = isDisabled;
		if (isDisabled) {
			this.nameControl.disable();
		} else {
			this.nameControl.enable();
		}
	}

	public get disabled(): boolean {
		return this._disabled;
	}

	@Output() public deviceNameChanged = new EventEmitter<string>();
	@Output() public errorMessageHeight = new EventEmitter<number>();

	public nameControl = new FormControl('', { validators: Validators.required, nonNullable: true });
	public matcher = new ShowOnDirtyErrorStateMatcher();

	public ngOnInit(): void {
		this.nameControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.emitHeight();
		});
	}

	public ngAfterViewInit(): void {
		this.errors.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.emitHeight();
		});
	}

	public onDeviceNameChange(deviceName: string | null): void {
		if (deviceName === null) {
			return;
		}
		if (this.nameControl.valid) {
			this.deviceNameChanged.emit(deviceName);
			this.inputElement.nativeElement?.blur();
		}
	}

	public onClick(event: Event): void {
		event.stopPropagation();
	}

	public onDoubleClick(event: Event): void {
		event.stopPropagation();
	}

	public onBlur(): void {
		this.emitHeight();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private emitHeight(): void {
		setTimeout(() => {
			const error = this.errors.find((e) => !!e);
			const errorHeight = error?.nativeElement.clientHeight;

			if (error && errorHeight) {
				this.errorMessageHeight.emit(errorHeight);
			} else {
				this.errorMessageHeight.emit(0);
			}
		});
	}
}
